/**
 * RevenueByMonthChart.tsx — THE "Revenue by month" chart, shared by Sales
 * Dashboard and Reports (§24 C-8). The two pages carried byte-copies that
 * differed by 20 px of height and one click handler; any fix landed twice.
 *
 * Built in recharts (the library stays: var() fills are proven, ADOPTION.md
 * §5.2, and the tooltip + month drill come for free) to the S-Tier k-chart
 * contract — see K_CHART in revenueByMonthView.ts for the numbers and their
 * klaxiora.css lines. What the contract asks for, and how each lands here:
 *   - mono axis on a 52 px gutter → YAxis width, font-mono on the wrapper
 *     (SVG text inherits font-family; the ticks and labels never carry a
 *     font attribute of their own)
 *   - five hairline gridlines on a --line baseline → CartesianGrid on the
 *     five nice ticks (solid --hair, no dash) + the XAxis line in --line
 *   - a 216 px plot → plotHeight; the container adds the label headroom and
 *     the category row
 *   - C-3: the current month `--accent`, every other month `--accentSoft`
 *   - C-4: a mono value label above every NON-ZERO bar, in the axis' unit
 *   - C-2: the axis formatter — one unit from the top tick, exact ticks
 *   - C-9: the tooltip carries the FULL figure (money(): £2,022,276.46),
 *     compact only on the axis and the label
 *   - C-5: an all-zero window renders the in-card empty state, not an axis
 *     of five "£0" gridlines
 * Height and the bar click are props; the Dashboard passes neither and gets
 * the contract's plot, the Reports page passes its month drill (pointer-only,
 * R7-3 — recharts bars are not keyboard-reachable; the known, flagged gap).
 */
import {
  BarChart, Bar, Cell, LabelList, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts'
import { EmptyState } from '../../DashboardShared'
import { money } from '../../../../lib/money'
import { accent, edge, ink, shadow, surface, track } from '../../../../theme/tokens'
import {
  K_CHART, REVENUE_EMPTY_MESSAGE, barValueLabel, revenueChartModel, type RevenueMonth,
} from './revenueByMonthView'

export function RevenueByMonthChart({ data, plotHeight = K_CHART.plotHeight, onBarClick, emptyStubs = false }: {
  /** The wire's six zero-filled months, ascending, last = current month. */
  data: RevenueMonth[]
  /** The plot's height in px — the k-chart contract's 216 unless a caller
   *  has a reason; the container adds the label headroom and category row. */
  plotHeight?: number
  /** A bar's month, for the caller's drill; absent → bars are inert. */
  onBarClick?: (month: RevenueMonth) => void
  /** Slice 3 (Sales Home): the boards' empty-month stubs — a short grey bar
   *  at the baseline of every zero month — with one note per run of them
   *  ("No closed-won deals April – July"). Off by default: Reports keeps the
   *  bare zero months it always drew. */
  emptyStubs?: boolean
}) {
  const m = revenueChartModel(data)
  if (m.allZero) return <EmptyState message={REVENUE_EMPTY_MESSAGE} />
  const height = plotHeight + K_CHART.marginTop + K_CHART.xAxisHeight
  const rows = emptyStubs ? m.rows.map(r => ({ ...r, stub: r.wonPence === 0 ? m.stubValue * (plotHeight / K_CHART.plotHeight) : 0 })) : m.rows
  const n = m.rows.length
  return (
    <div className="font-mono relative" data-chart="revenue-by-month">
      {/* The run notes sit over the plot as HTML, each centred on its run: the
          plot spans the container minus the axis gutter, and each month owns
          an equal band of it — no measurement, no layout effect. */}
      {emptyStubs && m.zeroRuns.map(run => (
        <div key={run.start} data-empty-run className="absolute text-13 text-ink-muted text-center pointer-events-none font-sans"
          style={{ left: `calc(${K_CHART.gutter}px + (100% - ${K_CHART.gutter}px) * ${run.start / n})`, width: `calc((100% - ${K_CHART.gutter}px) * ${(run.end - run.start + 1) / n})`, top: K_CHART.marginTop + plotHeight * 0.55 }}>
          {run.note}
        </div>
      ))}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={rows} margin={{ top: K_CHART.marginTop, right: 0, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={edge['row-faint']} />
          <XAxis dataKey="label" height={K_CHART.xAxisHeight} tick={{ fontSize: K_CHART.axisFontSize, fill: ink.muted }}
            axisLine={{ stroke: edge.control }} tickLine={false} />
          <YAxis width={K_CHART.gutter} ticks={m.axis.ticks} domain={m.axis.domain} tickFormatter={m.axis.format}
            tick={{ fontSize: K_CHART.axisFontSize, fill: ink.muted }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: surface.card, border: 'none', borderRadius: 10, fontSize: 12, boxShadow: shadow.tooltip }}
            formatter={(v: number) => money(v / 100)} cursor={{ fill: surface['row-hover'] }} />
          <Bar dataKey="wonPence" name="Won" radius={[4, 4, 0, 0]} maxBarSize={K_CHART.maxBarWidth} stackId={emptyStubs ? 'revenue' : undefined}
            className={onBarClick ? 'cursor-pointer' : undefined}
            onClick={onBarClick ? (bar) => {
              const p = (bar as { payload?: RevenueMonth }).payload
              if (p?.month != null && p.wonPence != null) onBarClick({ month: p.month, wonPence: p.wonPence })
            } : undefined}>
            {m.rows.map(r => <Cell key={r.month} fill={r.current ? accent.DEFAULT : accent.soft} />)}
            <LabelList dataKey="wonPence" position="top" offset={K_CHART.labelOffset}
              fill={ink.secondary} fontSize={K_CHART.labelFontSize}
              formatter={(v: unknown) => barValueLabel(v, m.axis)} />
          </Bar>
          {emptyStubs && (
            <Bar dataKey="stub" stackId="revenue" fill={track.DEFAULT} maxBarSize={K_CHART.maxBarWidth} isAnimationActive={false} tooltipType="none" />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
